import React from 'react';
import PropTypes from 'prop-types';

import FormError from './FormError';
import parseError from '../../utils/parseError';
import { trackAuthDdAction } from '../../utils/authDatadog';
import validator from '../../utils/validator';

class Form extends React.Component {
  static propTypes = {
    children: PropTypes.func.isRequired,
    name: PropTypes.string.isRequired,
    onSubmit: PropTypes.func.isRequired,
    onChange: PropTypes.func,
    error: PropTypes.shape({
      serverError: PropTypes.bool,
      msg: PropTypes.string,
    }),
    errorMsg: PropTypes.string,
    noValidate: PropTypes.bool,
    requiredFields: PropTypes.arrayOf(PropTypes.string),
    datadogActionName: PropTypes.string,
  }

  static defaultProps = {
    onChange: () => {},
    error: {
      serverError: false,
      msg: '',
      keys: [],
    },
    errorMsg: null,
    noValidate: undefined,
    requiredFields: undefined,
    datadogActionName: undefined,
  }

  state = {
    values: {},
    error: this.props.error, // eslint-disable-line react/destructuring-assignment
    processing: false,
    disabled: false,
    success: false,
  }

  mounted = false

  componentDidMount() {
    this.mounted = true;
  }

  componentDidUpdate(prevProps) {
    const { error: { msg: oldValue } } = prevProps;
    const { error: { msg: newValue } } = this.props;
    if (oldValue !== newValue) {
      this.showHideErrors();
    }
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  showHideErrors = () => {
    const { error } = this.props;
    this.setState({ error });
  }

  handleError = (err) => {
    const { errorMsg } = this.props;
    const error = parseError(err);
    const { data, keys, errorStatus } = error;
    this.setState({
      processing: false,
      error: {
        data,
        keys,
        msg: errorMsg || error.msg,
        serverError: true,
        errorStatus,
      },
    });
  }

  handleChange = (obj) => {
    const { onChange, name, requiredFields } = this.props;
    const { values } = this.state;
    const updatedValues = { ...values, ...obj };
    const { isValid } = validator(name, updatedValues, requiredFields);

    this.setState((state) => ({
      ...state,
      values: updatedValues,
      disabled: !isValid,
    }), () => {
      const { values: data } = this.state;
      onChange(data);
    });
  }

  onSubmit = async () => {
    const { onSubmit } = this.props;
    const { values } = this.state;

    try {
      await onSubmit(values);
      if (!this.mounted) return;

      this.setState({
        processing: false,
        error: {
          serverError: false,
          msg: '',
          keys: [],
        },
        success: true,
      });
    } catch (err) {
      this.handleError(err.data || err);
    }
  }

  handleSubmit = (e) => {
    e.preventDefault();
    const { datadogActionName } = this.props;
    trackAuthDdAction(datadogActionName, { source: 'auth' });

    const formData = new FormData(e.currentTarget);
    const values = Object.fromEntries(formData.entries());

    this.setState({
      values,
      processing: true,
      success: false,
    }, this.onSubmit);
  }

  renderInputs = () => {
    const { children } = this.props;
    const {
      disabled, error: { data, keys: errorKeys, errorStatus }, processing, success, values,
    } = this.state;

    return children({
      disabled,
      errorData: data,
      errorKeys,
      errorStatus,
      onChange: this.handleChange,
      processing,
      success,
      values,
    });
  }

  render() {
    const { name, noValidate } = this.props;
    const { error: { serverError, msg } } = this.state;

    return (
      <div>
        <form
          className="Form"
          name={name}
          onSubmit={this.handleSubmit}
          noValidate={noValidate}
        >
          {this.renderInputs()}
        </form>
        {serverError && (
          <FormError>
            {msg}
          </FormError>
        )}
      </div>
    );
  }
}

Form.displayName = 'Form';
export default Form;
